// Considered: IconPickerAtom — rejected as the panel body because it IS an overlay
// behind its own trigger button; composing it inside this picker's panel would put a
// second trigger and a second overlay where the contract has an inline search + grid
// (`ident > group > field:glyph > <GlyphPicker> > each(hits)`). Its whole-collection,
// filename-only search and the letter/hex tabs have no equivalent there either.
// GlyphAtom is composed for the mark and HuePickerMolecule for the brand hue.
// See spec Design System Reuse Audit; the IconPickerAtom conflict is recorded in
// .ralph/PROGRESS.md.
import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Icon } from '@iconify/react';
import { useTranslation } from 'localization';
import type { GlyphColor } from '../../../types/glyph';
import GlyphAtom from '../../atoms/Glyph/GlyphAtom';
import InputAtom from '../../atoms/Input/InputAtom';
import { useDialogFocus } from '../../../hooks/useDialogFocus';
import HuePickerMolecule from '../marketplace/HuePickerMolecule';
import { GLYPH_PICKER_TOKENS, isLetterGlyph, searchGlyphIcons } from './glyphPickerVocabulary';
import './GlyphPickerMolecule.scss';

/** The typed value as a stored hex colour (`#` plus six hex digits), or null while it is not one. */
function toHexColour(typed: string): string | null {
  const trimmed = typed.trim();
  const hex = trimmed.startsWith('#') ? trimmed : `#${trimmed}`;
  return /^#[0-9a-fA-F]{6}$/.test(hex) ? hex : null;
}

export type GlyphPickerTab = 'icon' | 'letter' | 'color';

/** The hue the slider rests at before a hue has actually been chosen (prototype default). */
const DEFAULT_HUE = 210;
/** The mark a cleared letter falls back to — a glyph is never absent, only neutral. */
const FALLBACK_ICON = 'lucide:circle';

/**
 * The contract datums this picker renders, supplied by the caller.
 *
 * The picker serves more than one block (Identity's mark, Placement's new-family
 * mark), and the decomposition names each block's leaves separately — so the ids
 * are a prop rather than baked in. A slot left undefined emits no `data-datum`.
 */
export interface GlyphPickerDatums {
  tile?: string;
  mark?: string;
  /**
   * The tile's own letter, when the contract addresses it as a distinct leaf from
   * `mark` (`ident.glyph-text` / `place.glyph-text`) — an icon-rendered tile has no
   * letter and emits nothing for this slot.
   */
  markText?: string;
  hint?: string;
  preview?: string;
  previewMark?: string;
  tab?: string;
  search?: string;
  iconResult?: string;
  noHits?: string;
  iconNote?: string;
  letterHint?: string;
  letterInput?: string;
  useIcon?: string;
  themeLabel?: string;
  swatch?: string;
  hueLabel?: string;
  hueSlider?: string;
  hueValue?: string;
  exactLabel?: string;
  hexInput?: string;
  backToToken?: string;
  /** The "Picked for you" / "You chose this" chip beside the tile. */
  pickedForYou?: string;
  /** The "Default from …" / "Overrides the default" line beneath that chip. */
  overridesDefault?: string;
  /** The "Back to default" action, shown only when the mark is set (not derived). */
  backToDefault?: string;
}

export interface GlyphPickerMoleculeProps {
  /** Iconify `set:name` or a single letter — the value as it is stored on the schema. */
  glyph: string | null;
  glyphColor?: GlyphColor | null;
  /** Seeds the letter fallback when no glyph is set (typically the type's name). */
  fallbackText?: string;
  onChange?: (next: { glyph: string | null; glyphColor: GlyphColor | null }) => void;
  /** Static mark, no panel, no hint — the locked treatment. */
  readOnly?: boolean;
  /** Overrides the default explanation beneath the tile. */
  hint?: React.ReactNode;
  /** Suppresses the hint where the surrounding field already explains the mark. */
  hideHint?: boolean;
  /**
   * True when the mark was never explicitly set and is standing in on a default
   * (e.g. the family's mark). Swaps the provenance chip/hint/action beside the
   * tile between "picked for you" (derived) and "you chose this" (set), and
   * hides the "back to default" action once there is nothing to revert.
   */
  derived?: boolean;
  /** Named for the "Default from {{source}}" hint when `derived` is true. */
  derivedFrom?: string;
  /** Overrides the provenance hint text when derived is true. Replaces "Default from {{source}}". */
  provenanceLabel?: string;
  /** Overrides the reset button label. Replaces "Back to default". */
  resetLabel?: string;
  /** Called when the reset action is clicked. When set, `onChange` is not called on reset. */
  onReset?: () => void;
  /** What "back to default" reverts to. Absent means revert to no mark at all. */
  defaultGlyph?: string | null;
  defaultGlyphColor?: GlyphColor | null;
  /**
   * Tile edge in px. The composer's Identity row uses 44 (`aspect-blocks.jsx:50`'s
   * `.a-glyph-tile`, the node the contract binds `ident.tile` to — NOT the 34px
   * `window.GlyphPicker`/`.g-tile` the prototype's own Identity call site passes;
   * script load order in the prototype shadows that call's `tileSize` prop, so the
   * rendered/captured contract is the 44px tile).
   */
  tileSize?: number;
  /**
   * Opens the panel on mount. The panel is otherwise self-managed; this is the
   * seam a story (or a test) uses to render an interaction state without driving
   * a click, and the reason the picker's open states are capturable at all.
   */
  defaultOpen?: boolean;
  defaultTab?: GlyphPickerTab;
  /** Seeds the icon search, so the "nothing matches" branch is reachable declaratively. */
  defaultQuery?: string;
  datums?: GlyphPickerDatums;
  /**
   * When the brand hue is emitted: `live` follows the slider as it moves; `release` previews the
   * hue in the picker and emits it once, when the slider is let go. A caller that saves each
   * change uses `release`, so dragging the slider is one save rather than one per degree.
   */
  hueCommit?: 'live' | 'release';
  /**
   * Makes every choice in the panel inert, with this reason as each control's tooltip, while the
   * panel stays open and keyboard focus stays where it was. Used while a change is being saved.
   */
  disabledReason?: string;
  className?: string;
  'data-testid'?: string;
}

const TABS: GlyphPickerTab[] = ['icon', 'letter', 'color'];
const TAB_LABEL_KEY: Record<GlyphPickerTab, string> = {
  icon: 'studio.typeEditor.glyphPicker.tab.icon',
  letter: 'studio.typeEditor.glyphPicker.tab.letter',
  color: 'studio.typeEditor.glyphPicker.tab.color',
};

const GlyphPickerMolecule: React.FC<GlyphPickerMoleculeProps> = ({
  glyph,
  glyphColor,
  fallbackText,
  onChange,
  readOnly = false,
  hint,
  hideHint = false,
  derived = false,
  derivedFrom,
  provenanceLabel,
  resetLabel,
  onReset,
  defaultGlyph = null,
  defaultGlyphColor = null,
  tileSize,
  defaultOpen = false,
  defaultTab = 'icon',
  defaultQuery = '',
  datums = {},
  hueCommit = 'live',
  disabledReason,
  className = '',
  'data-testid': dataTestId = 'glyph-picker',
}) => {
  const { t } = useTranslation();
  const rootRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(defaultOpen);
  const [tab, setTab] = useState<GlyphPickerTab>(defaultTab);
  const [query, setQuery] = useState(defaultQuery);
  const [draftHue, setDraftHue] = useState<number | null>(null);
  // The exact colour as typed; it reaches `onChange` only once it is a complete hex. A draft left
  // incomplete stays in the field with an error saying what a hex needs, until it is fixed or cleared.
  const [hexDraft, setHexDraft] = useState<string | null>(null);
  const [hexInvalid, setHexInvalid] = useState(false);
  const disabled = !!disabledReason;
  // Controls stay focusable while disabled, so a keyboard user keeps their place mid-save.
  const inert = disabled ? { 'aria-disabled': true, title: disabledReason } : {};
  const panelId = `glyph-picker-panel-${useId()}`;
  const hexErrorId = `glyph-picker-hex-error-${useId()}`;

  // Escape and focus management come from the shared hook every other overlay in
  // this surface uses, so the panel dismisses and returns focus to its tile the
  // same way the sheets do. `trapFocus: false` because the panel opens in place
  // and leaves the page reachable — the hook then scopes Escape to keys raised
  // inside the panel, which is sound here because `manageFocus` has already moved
  // focus into it. Click-away stays local: the hook has no pointer-dismiss option,
  // and the gesture is bounded by `rootRef` (trigger + panel) rather than the panel
  // alone, so clicking the tile toggles rather than closing and reopening.
  const dismiss = useCallback(() => setOpen(false), []);
  useDialogFocus(open && !readOnly, panelRef, { onDismiss: dismiss, trapFocus: false });

  useEffect(() => {
    if (!open) return undefined;
    const away = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, [open]);

  const isLetter = isLetterGlyph(glyph);
  const hits = searchGlyphIcons(query);
  // A colour chosen any other way (a swatch, the hue, back to a theme colour) replaces what was being
  // typed, so the field shows the colour in use and drops the error about the abandoned draft.
  const emit = useCallback(
    (patch: { glyph?: string | null; glyphColor?: GlyphColor | null }) => {
      if (disabled) return;
      if (patch.glyphColor !== undefined) {
        setHexDraft(null);
        setHexInvalid(false);
      }
      onChange?.({
        glyph: patch.glyph !== undefined ? patch.glyph : glyph,
        glyphColor: patch.glyphColor !== undefined ? patch.glyphColor : glyphColor ?? null,
      });
    },
    [glyph, glyphColor, onChange, disabled]
  );
  // The same when the colour changes from outside the picker (an undo, another editor). Keyed by value,
  // so a caller that rebuilds the same colour object on every render never wipes a draft mid-typing.
  const colourKey = glyphColor ? `${glyphColor.kind}:${glyphColor.value}` : '';
  useEffect(() => {
    setHexDraft(null);
    setHexInvalid(false);
  }, [colourKey]);
  const commitHue = (hue: number) => {
    setDraftHue(null);
    emit({ glyphColor: { kind: 'hue', value: hue } });
  };
  // The hue being dragged, before it is released, is what the tile and preview paint.
  const shownColor: GlyphColor | null | undefined = draftHue === null ? glyphColor : { kind: 'hue', value: draftHue };

  const tileStyle = tileSize ? { width: tileSize, height: tileSize } : undefined;
  let colourSummary = t('studio.typeEditor.glyphPicker.noColour');
  if (glyphColor?.kind === 'token' || glyphColor?.kind === 'hex') colourSummary = glyphColor.value;
  else if (glyphColor?.kind === 'hue') colourSummary = t('studio.typeEditor.glyphPicker.hueSummary', { hue: glyphColor.value });
  const triggerLabel = t('studio.typeEditor.glyphPicker.trigger', { colour: colourSummary });

  return (
    <div className={['glyph-picker', className].filter(Boolean).join(' ')} ref={rootRef} data-testid={dataTestId}>
      <button
        type="button"
        className="glyph-picker__tile"
        style={tileStyle}
        disabled={readOnly}
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        aria-label={triggerLabel}
        title={triggerLabel}
        onClick={() => setOpen((o) => !o)}
        data-datum={datums.tile}
        data-kind="tile"
      >
        <GlyphAtom
          glyph={glyph}
          glyphColor={shownColor ?? undefined}
          fallbackText={fallbackText}
          aria-hidden
          data-datum={datums.mark}
          data-kind="glyph"
          data-letter-datum={datums.markText}
          data-letter-kind="text"
        />
      </button>

      {!readOnly && (datums.pickedForYou || datums.overridesDefault || datums.backToDefault) && (
        <div className="glyph-picker__side">
          <span
            className={`glyph-picker__provenance-chip glyph-picker__provenance-chip--${derived ? 'derived' : 'set'}`}
            data-datum={datums.pickedForYou}
            data-kind="chip"
          >
            <i className={`pi ${derived ? 'pi-sitemap' : 'pi-user'}`} aria-hidden="true" />
            {derived ? t('studio.typeEditor.glyphPicker.pickedForYou') : t('studio.typeEditor.glyphPicker.chosenByYou')}
          </span>
          <span className="glyph-picker__hint" data-datum={datums.overridesDefault} data-kind="hint">
            {provenanceLabel ||
              (derived
                ? t('studio.typeEditor.glyphPicker.defaultFrom', { source: derivedFrom ?? '' })
                : t('studio.typeEditor.glyphPicker.overridesDefault'))}
          </span>
          {!derived && (
            <button
              type="button"
              className="glyph-picker__quiet-action"
              {...inert}
              onClick={() => {
                if (disabled) return;
                if (onReset) {
                  onReset();
                } else {
                  emit({ glyph: defaultGlyph, glyphColor: defaultGlyphColor });
                }
              }}
              data-datum={datums.backToDefault}
            >
              {resetLabel || t('studio.typeEditor.glyphPicker.backToDefault')}
            </button>
          )}
        </div>
      )}

      {!readOnly && !hideHint && (
        <span className="glyph-picker__hint" data-datum={datums.hint} data-kind="hint">
          {hint ?? t('studio.typeEditor.glyphPicker.hint')}
        </span>
      )}

      {open && !readOnly && (
        <div
          ref={panelRef}
          className="glyph-picker__panel"
          id={panelId}
          role="group"
          aria-label={t('studio.typeEditor.glyphPicker.panelAriaLabel')}
        >
          <div className="glyph-picker__panel-head">
            <span className="glyph-picker__preview" data-datum={datums.preview} data-kind="text">
              <GlyphAtom
                glyph={glyph}
                glyphColor={shownColor ?? undefined}
                fallbackText={fallbackText}
                aria-hidden
                data-datum={datums.previewMark}
                data-kind="glyph"
              />
            </span>
            <div className="glyph-picker__tabs" role="tablist" aria-label={t('studio.typeEditor.glyphPicker.panelAriaLabel')}>
              {TABS.map((id) => (
                <button
                  key={id}
                  type="button"
                  role="tab"
                  className="glyph-picker__tab"
                  data-container="each(tabs)"
                  aria-selected={tab === id}
                  data-on={tab === id ? '1' : undefined}
                  onClick={() => setTab(id)}
                  data-datum={datums.tab}
                >
                  {t(TAB_LABEL_KEY[id])}
                </button>
              ))}
            </div>
          </div>

          {tab === 'icon' && (
            <>
              <div className="glyph-picker__search">
                <i className="pi pi-search" aria-hidden="true" />
                <InputAtom
                  value={query}
                  placeholder={t('studio.typeEditor.glyphPicker.searchPlaceholder')}
                  aria-label={t('studio.typeEditor.glyphPicker.searchPlaceholder')}
                  onChange={(e) => setQuery(e.target.value)}
                  data-datum={datums.search}
                />
              </div>
              <div className="glyph-picker__grid">
                {hits.map((icon) => (
                  <button
                    key={icon.name}
                    type="button"
                    className="glyph-picker__result"
                    data-container="each(hits)"
                    title={icon.name}
                    aria-label={icon.name}
                    aria-pressed={!isLetter && glyph === icon.name}
                    {...inert}
                    data-on={!isLetter && glyph === icon.name ? '1' : undefined}
                    onClick={() => emit({ glyph: icon.name })}
                    data-datum={datums.iconResult}
                  >
                    <Icon icon={icon.name} aria-hidden />
                  </button>
                ))}
                {!hits.length && (
                  <p className="glyph-picker__no-hits" data-datum={datums.noHits} data-kind="note">
                    {t('studio.typeEditor.glyphPicker.noHits', { query })}
                  </p>
                )}
              </div>
              <p className="glyph-picker__note" data-datum={datums.iconNote} data-kind="text">
                {isLetter
                  ? t('studio.typeEditor.glyphPicker.letterAlwaysWorks')
                  : t('studio.typeEditor.glyphPicker.searchByMeaning')}
              </p>
            </>
          )}

          {tab === 'letter' && (
            <div className="glyph-picker__letter">
              <p className="glyph-picker__hint" data-datum={datums.letterHint} data-kind="hint">
                {t('studio.typeEditor.glyphPicker.letterHint')}
              </p>
              <InputAtom
                className="glyph-picker__letter-input"
                maxLength={1}
                placeholder={t('studio.typeEditor.glyphPicker.letterPlaceholder')}
                aria-label={t('studio.typeEditor.glyphPicker.letterAriaLabel')}
                value={isLetter ? glyph ?? '' : ''}
                readOnly={disabled}
                {...inert}
                onChange={(e) => {
                  const next = e.target.value.slice(0, 1).toUpperCase();
                  emit({ glyph: next || FALLBACK_ICON });
                }}
                data-datum={datums.letterInput}
              />
              {isLetter && (
                <button
                  type="button"
                  className="glyph-picker__quiet-action"
                  {...inert}
                  onClick={() => emit({ glyph: FALLBACK_ICON })}
                  data-datum={datums.useIcon}
                >
                  {t('studio.typeEditor.glyphPicker.useAnIconInstead')}
                </button>
              )}
            </div>
          )}

          {tab === 'color' && (
            <div className="glyph-picker__color">
              {/*
                Tokens first, named, and the only option with a plain label: they
                follow light and dark mode, so they are the path that keeps working.
              */}
              <div className="glyph-picker__color-section">
                <span className="glyph-picker__color-label" data-datum={datums.themeLabel} data-kind="text">
                  {t('studio.typeEditor.glyphPicker.themeColour')}
                  <i>{t('studio.typeEditor.glyphPicker.themeColourNote')}</i>
                </span>
                <div className="glyph-picker__swatches">
                  {GLYPH_PICKER_TOKENS.map((swatch) => (
                    <button
                      key={swatch.token}
                      type="button"
                      className="glyph-picker__swatch"
                      data-container="each(tokens)"
                      style={{ '--glyph-picker-swatch': `var(--glyph-token-${swatch.token})` } as React.CSSProperties}
                      title={t(swatch.labelKey)}
                      aria-label={t(swatch.labelKey)}
                      {...inert}
                      aria-pressed={glyphColor?.kind === 'token' && glyphColor.value === swatch.token}
                      data-on={glyphColor?.kind === 'token' && glyphColor.value === swatch.token ? '1' : undefined}
                      onClick={() => emit({ glyphColor: { kind: 'token', value: swatch.token } })}
                      data-datum={datums.swatch}
                    />
                  ))}
                </div>
              </div>

              <div className="glyph-picker__color-section">
                <span className="glyph-picker__color-label" data-datum={datums.hueLabel} data-kind="text">
                  {t('studio.typeEditor.glyphPicker.brandHue')}
                  <i>{t('studio.typeEditor.glyphPicker.brandHueNote')}</i>
                </span>
                <HuePickerMolecule
                  value={shownColor?.kind === 'hue' ? shownColor.value : DEFAULT_HUE}
                  onChange={(hue) => (hueCommit === 'release' ? setDraftHue(hue) : commitHue(hue))}
                  onChangeEnd={hueCommit === 'release' ? commitHue : undefined}
                  disabledReason={disabledReason}
                  valueText={
                    shownColor?.kind === 'hue'
                      ? t('studio.typeEditor.glyphPicker.hueSummary', { hue: shownColor.value })
                      : t('studio.typeEditor.glyphPicker.hueNotInUse')
                  }
                  sliderDatum={datums.hueSlider}
                  valueDatum={datums.hueValue}
                  showPresets={false}
                />
              </div>

              <div className="glyph-picker__color-section">
                <span className="glyph-picker__color-label" data-datum={datums.exactLabel} data-kind="text">
                  {t('studio.typeEditor.glyphPicker.exactColour')}
                  <i>{t('studio.typeEditor.glyphPicker.exactColourNote')}</i>
                </span>
                <div className="glyph-picker__hex-row">
                  <InputAtom
                    className="glyph-picker__hex"
                    placeholder={t('studio.typeEditor.glyphPicker.hexPlaceholder')}
                    aria-label={t('studio.typeEditor.glyphPicker.exactColour')}
                    value={hexDraft ?? (glyphColor?.kind === 'hex' ? glyphColor.value : '')}
                    maxLength={7}
                    spellCheck={false}
                    autoComplete="off"
                    aria-invalid={hexInvalid || undefined}
                    aria-describedby={hexInvalid ? hexErrorId : undefined}
                    readOnly={disabled}
                    {...inert}
                    onChange={(e) => {
                      const hex = toHexColour(e.target.value);
                      // Only a COMPLETE hex is committed; a half-typed one would
                      // repaint the mark to a colour the author never chose.
                      if (hex) {
                        setHexDraft(null);
                        setHexInvalid(false);
                        emit({ glyphColor: { kind: 'hex', value: hex } });
                      } else {
                        setHexDraft(e.target.value);
                      }
                    }}
                    onBlur={() => {
                      if (hexDraft === null) return;
                      if (hexDraft.trim() === '') {
                        setHexDraft(null);
                        setHexInvalid(false);
                      } else {
                        setHexInvalid(true);
                      }
                    }}
                    data-datum={datums.hexInput}
                  />
                  {glyphColor && glyphColor.kind !== 'token' && (
                    <button
                      type="button"
                      className="glyph-picker__quiet-action"
                      {...inert}
                      onClick={() => emit({ glyphColor: { kind: 'token', value: 'neutral' } })}
                      data-datum={datums.backToToken}
                    >
                      {t('studio.typeEditor.glyphPicker.backToThemeColour')}
                    </button>
                  )}
                  {hexInvalid && (
                    <span id={hexErrorId} className="glyph-picker__hex-error" role="alert">
                      {t('studio.typeEditor.glyphPicker.hexInvalid')}
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

GlyphPickerMolecule.displayName = 'GlyphPickerMolecule';

export default GlyphPickerMolecule;
